import { useEffect, useState } from "react";
import { Pencil, Plus, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { ServiceCard } from "@/components/schedora/ServiceCard";
import { useApp, useOwnerSnapshot } from "@/context/AppContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const blankForm = {
  name: "",
  category: "",
  duration: 60,
  price: 0,
  description: "",
  active: true,
};

export default function OwnerServices() {
  const snapshot = useOwnerSnapshot(new Date().toISOString().slice(0, 10));
  const { addService, updateService, deleteService } = useApp();
  const [open, setOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState(blankForm);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (!editingId) {
      setForm(blankForm);
      return;
    }

    const service = snapshot?.services.find((entry) => entry.id === editingId);
    if (service) {
      setForm({
        name: service.name,
        category: service.category,
        duration: service.duration,
        price: service.price,
        description: service.description,
        active: service.active,
      });
    }
  }, [editingId, snapshot?.services]);

  const submit = async () => {
    setError("");
    setMessage("");
    if (!form.name.trim() || !form.category.trim()) {
      setError("Name and category are required.");
      return;
    }
    if (form.duration <= 0) {
      setError("Duration must be greater than zero.");
      return;
    }
    if (form.price < 0) {
      setError("Price cannot be negative.");
      return;
    }
    setIsSaving(true);
    if (editingId) {
      await updateService(editingId, form);
      setMessage("Service updated.");
    } else {
      await addService(form);
      setMessage("Service added.");
    }
    setIsSaving(false);
    setOpen(false);
    setEditingId(null);
    setForm(blankForm);
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-heading font-bold text-foreground">Services</h1>
          <p className="text-sm text-muted-foreground">{snapshot?.services.length ?? 0} active and archived services</p>
        </div>
        <Dialog open={open} onOpenChange={(nextOpen) => {
          setOpen(nextOpen);
          if (!nextOpen) {
            setEditingId(null);
            setForm(blankForm);
          }
        }}>
          <DialogTrigger asChild>
            <Button size="sm"><Plus className="mr-1 h-4 w-4" /> Add Service</Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle className="font-heading">{editingId ? "Edit Service" : "Add Service"}</DialogTitle></DialogHeader>
            <div className="space-y-4 pt-2">
              <div><Label>Service Name</Label><Input value={form.name} onChange={(event) => setForm({ ...form, name: event.target.value })} className="mt-1" /></div>
              <div><Label>Category</Label><Input value={form.category} onChange={(event) => setForm({ ...form, category: event.target.value })} className="mt-1" /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Duration (min)</Label><Input type="number" value={form.duration} onChange={(event) => setForm({ ...form, duration: Number(event.target.value) })} className="mt-1" /></div>
                <div><Label>Price ($)</Label><Input type="number" value={form.price} onChange={(event) => setForm({ ...form, price: Number(event.target.value) })} className="mt-1" /></div>
              </div>
              <div><Label>Description</Label><Input value={form.description} onChange={(event) => setForm({ ...form, description: event.target.value })} className="mt-1" /></div>
              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" checked={form.active} onChange={(event) => setForm({ ...form, active: event.target.checked })} />
                Service is bookable
              </label>
              {error && <p className="text-sm text-destructive">{error}</p>}
              <Button className="w-full" onClick={() => void submit()} disabled={isSaving}>
                {isSaving ? "Saving..." : editingId ? "Save Changes" : "Add Service"}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>
      {message && <p className="text-sm text-success">{message}</p>}

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {snapshot?.services.map((service) => (
          <div key={service.id} className="space-y-3">
            <ServiceCard {...service} />
            <div className="flex gap-2">
              <Button size="sm" variant="outline" onClick={() => { setEditingId(service.id); setOpen(true); }}>
                <Pencil className="mr-1 h-4 w-4" /> Edit
              </Button>
              <Button size="sm" variant="ghost" className="text-destructive" onClick={async () => { await deleteService(service.id); setMessage("Service deleted."); }}>
                <Trash2 className="mr-1 h-4 w-4" /> Delete
              </Button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
